import { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Clock, CheckCircle2, AlertTriangle, XCircle, Calendar, Play, Skull, Loader2, FileText } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * AdminCronHistoryDropdown — Round 92o++++++++N++++++++ (May 2026).
 *
 * Replaces the manual admin warmer buttons that have been moved to
 * the daily/weekly cron orchestrator. Shows per-job:
 *   • last started / completed / duration / result
 *   • a tiny 7-bar "last 7 runs" sparkline so regressions stand out
 *
 * Reads from `/api/admin/cron-history` which surfaces every row in
 * the `admin_cron_runs` collection.
 *
 * Polls every 60s while open so the admin can watch a chain run
 * live during the 2 AM window.
 */
const RESULT_STYLES = {
  ok: { icon: CheckCircle2, color: 'text-emerald-600 dark:text-emerald-400', label: 'OK' },
  stalled: { icon: AlertTriangle, color: 'text-amber-600 dark:text-amber-400', label: 'Stalled' },
  error: { icon: XCircle, color: 'text-rose-600 dark:text-rose-400', label: 'Error' },
  abandoned: { icon: XCircle, color: 'text-rose-700 dark:text-rose-500', label: 'Abandoned' },
  running: { icon: Clock, color: 'text-sky-600 dark:text-sky-400', label: 'Running' },
  // Round 94k — Zombie cron-run reaped by the startup or manual sweep.
  // Shown with a skull glyph + amber tint so it's instantly visible the
  // run died (didn't complete) without looking like a stalled-but-still-
  // valid result.
  crashed_recovered: { icon: Skull, color: 'text-amber-700 dark:text-amber-400', label: 'Crashed' },
  pending: { icon: Clock, color: 'text-zinc-400 dark:text-zinc-500', label: 'Never run' },
};

const BAR_COLORS = {
  ok: 'bg-emerald-500',
  stalled: 'bg-amber-500',
  error: 'bg-rose-500',
  abandoned: 'bg-rose-700',
  running: 'bg-sky-500',
};

function fmtDuration(seconds) {
  if (seconds == null) return '—';
  const s = Math.round(Number(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  const rem = s - m * 60;
  return rem === 0 ? `${m}m` : `${m}m ${rem}s`;
}
function fmtClock(iso) {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return iso; }
}

/**
 * SingleJobRunButton — Round 92o++++++++N++++++++++ (May 2026 admin-UX
 * upgrade). Tiny inline ▶ button that fires a single warmer/backfill
 * from the per-row strip. Two-stage confirm to prevent the admin from
 * accidentally re-firing a 9-minute 13-F job just by mis-clicking the
 * row.
 *
 * Round P108ao (Feb 2026) — respects the row's live `running` state:
 * when the latest run is still running, the button renders as a
 * disabled "Running…" pill instead of firing a second copy. Backend
 * also returns 409 in that case (belt-and-suspenders); if a stale
 * client somehow gets past the disabled state, we surface the 409's
 * message via toast instead of a silent no-op.
 */
/**
 * Sep 2026 — stacking pre-flight for the manual Run buttons. Asks the
 * backend what is running right now and whether we're inside the 2–7 AM
 * ET warmer block; returns a short warning string (or null when clear).
 * Stacking a second SEC crawler on the 0.5 vCPU production box is what
 * killed the SEC Coverage / Warrants expiry runs.
 */
async function fetchStackingWarning() {
  try {
    const { data } = await axios.get(`${API}/admin/cron/load`, { withCredentials: true, timeout: 8_000 });
    const parts = [];
    const running = Array.isArray(data?.running) ? data.running : [];
    if (running.length) {
      const names = running.slice(0, 3).map((r) => r.label || r.job_key).join(', ');
      parts.push(`${running.length} job${running.length === 1 ? '' : 's'} running now (${names}${running.length > 3 ? ', …' : ''})`);
    }
    if (data?.heavy_window) parts.push(`it's ${data.now_et} — inside the ${data.heavy_window_label}`);
    if (data?.cgroup_limit_mb && data?.cgroup_mb && data.cgroup_mb / data.cgroup_limit_mb >= 0.75) {
      parts.push(`memory ${Math.round(data.cgroup_mb)}/${Math.round(data.cgroup_limit_mb)} MB`);
    }
    return parts.length ? `${parts.join(' · ')}. Stacking another job on the 0.5 vCPU box is what killed the last runs.` : null;
  } catch {
    return null;
  }
}

function StackingWarning({ text, testId }) {
  if (!text) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-semibold text-amber-700 dark:text-amber-300 max-w-[260px] leading-tight"
      title={text}
      data-testid={testId}
    >
      <AlertTriangle className="w-3 h-3 shrink-0" />
      <span className="truncate">{text}</span>
    </span>
  );
}

function SingleJobRunButton({ jobKey, label, onAfterRun, isRunning }) {
  const [stage, setStage] = useState('idle'); // 'idle' | 'confirm' | 'busy'
  const [warning, setWarning] = useState(null);
  const fire = async (e) => {
    e?.stopPropagation();
    if (isRunning) return;
    if (stage === 'idle') {
      setStage('confirm');
      setWarning(await fetchStackingWarning());
      return;
    }
    if (stage === 'busy') return;
    setStage('busy');
    setWarning(null);
    try {
      await axios.post(`${API}/admin/cron/run/job/${jobKey}`, {}, { withCredentials: true });
      setTimeout(() => onAfterRun?.(), 3_500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const message = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' && detail)
        || `Single-job run failed for ${jobKey}`;
      console.error(`Single-job run failed for ${jobKey}:`, err);
      try { toast?.error?.(message); } catch { /* toast optional */ }
    } finally {
      setTimeout(() => setStage('idle'), 4_500);
    }
  };
  if (isRunning) {
    return (
      <span
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold border border-sky-500/60 bg-sky-500/10 text-sky-700 dark:text-sky-300 whitespace-nowrap"
        title={`${label} is already running — button re-enables when the run finishes.`}
        data-testid={`single-job-running-${jobKey}`}
      >
        <Loader2 className="w-2.5 h-2.5 animate-spin" />
        Running…
      </span>
    );
  }
  if (stage === 'busy') {
    return (
      <span className="text-[10px] text-sky-600 dark:text-sky-400 font-semibold whitespace-nowrap" data-testid={`single-job-busy-${jobKey}`}>
        Starting…
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1">
      <button
        type="button"
        onClick={fire}
        title={stage === 'confirm' ? `Confirm: run ${label} now` : `Re-run just this job (${label})`}
        className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold border transition-colors
          ${stage === 'confirm'
            ? 'bg-rose-600 text-white border-rose-700 hover:bg-rose-700'
            : 'bg-background border-border hover:border-violet-400 hover:text-violet-600 dark:hover:text-violet-300'}`}
        data-testid={`single-job-run-${jobKey}`}
      >
        <Play className="w-2.5 h-2.5" />
        {stage === 'confirm' ? (warning ? 'Run anyway' : 'Confirm') : 'Run'}
      </button>
      {stage === 'confirm' && (
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); setStage('idle'); setWarning(null); }}
          className="text-[9px] text-muted-foreground hover:text-foreground underline"
          data-testid={`single-job-cancel-${jobKey}`}
        >
          cancel
        </button>
      )}
      {stage === 'confirm' && <StackingWarning text={warning} testId={`single-job-stacking-warning-${jobKey}`} />}
    </span>
  );
}

function MiniSparkline({ runs }) {
  // Bars from oldest → newest, height proportional to elapsed seconds.
  const ordered = useMemo(() => [...(runs || [])].reverse(), [runs]);
  if (!ordered.length) return null;
  const maxElapsed = Math.max(1, ...ordered.map(r => r.elapsed_s || 0));
  return (
    <div className="flex items-end gap-[2px] h-6" title={`Last ${ordered.length} runs`}>
      {ordered.map((r, idx) => {
        const h = Math.max(3, Math.round(((r.elapsed_s || 0) / maxElapsed) * 24));
        const color = BAR_COLORS[r.result] || BAR_COLORS.error;
        return (
          <div
            key={r.run_id || idx}
            className={`${color} w-[6px] rounded-sm`}
            style={{ height: `${h}px` }}
            title={`${r.result || 'unknown'} · ${fmtDuration(r.elapsed_s)} · ${fmtClock(r.started_at)}`}
          />
        );
      })}
    </div>
  );
}

function ChainRunButtons({ onAfterRun, runningByChain }) {
  const [busy, setBusy] = useState(null); // 'daily' | 'weekly' | null
  const [confirm, setConfirm] = useState(null); // 'daily' | 'weekly' | null
  const [warning, setWarning] = useState(null);
  const arm = async (chain) => {
    setConfirm(chain);
    setWarning(await fetchStackingWarning());
  };
  const fire = async (chain) => {
    setBusy(chain);
    setConfirm(null);
    setWarning(null);
    try {
      await axios.post(`${API}/admin/cron/run/${chain}`, {}, { withCredentials: true });
      // Refresh the history list ~5s later so the new "running"
      // row has time to appear in `admin_cron_runs`.
      setTimeout(() => onAfterRun?.(), 5_000);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const message = (detail && typeof detail === 'object' && detail.message)
        || (typeof detail === 'string' && detail)
        || `Failed to start ${chain} chain`;
      console.error(`Failed to start ${chain} chain:`, e);
      try { toast?.error?.(message); } catch { /* toast optional */ }
    } finally {
      setBusy(null);
    }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap pb-2 mb-1 border-b border-border/60">
      <span className="text-[11px] text-muted-foreground font-medium">Fire chain now:</span>
      {['daily', 'weekly', 'monthly', 'warrants_friday'].map((c) => {
        const isConfirm = confirm === c;
        const isBusy = busy === c;
        // Round P108ao — disable the chain button when the chain
        // already has a live in-flight job. Backend also returns 409
        // in that case, but disabling here saves a round trip and
        // makes the state obvious.
        const chainRunning = !!runningByChain?.[c];
        return (
          <button
            key={c}
            type="button"
            disabled={isBusy || (busy !== null && busy !== c) || chainRunning}
            onClick={() => (isConfirm ? fire(c) : arm(c))}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-md text-[11px] font-semibold border transition-colors
              ${isConfirm
                ? 'bg-rose-600 text-white border-rose-700 hover:bg-rose-700'
                : 'bg-background border-border hover:border-violet-400 hover:text-violet-600 dark:hover:text-violet-300'}
              disabled:opacity-50 disabled:cursor-not-allowed`}
            data-testid={`run-chain-${c}${chainRunning ? '-running' : ''}`}
            title={chainRunning
              ? `${c} chain has a job already running — button re-enables when it finishes.`
              : `Run the ${c} chain in the background now. Progress will appear in this dropdown.`}
          >
            <Play className="w-3 h-3" />
            {chainRunning
              ? `${c}: running…`
              : isBusy ? 'Starting…' : isConfirm ? (warning ? `Run ${c} anyway` : `Confirm: run ${c}`) : `Run ${c}`}
          </button>
        );
      })}
      {confirm && !busy && (
        <button
          type="button"
          onClick={() => { setConfirm(null); setWarning(null); }}
          className="text-[10px] text-muted-foreground hover:text-foreground underline"
        >
          cancel
        </button>
      )}
      {confirm && !busy && <StackingWarning text={warning} testId={`run-chain-stacking-warning-${confirm}`} />}
    </div>
  );
}


// Round 94k — Manual zombie reaper button. Marks any cron run that's
// been "running" >1h with no `finished_at` as crashed_recovered so the
// Cron History UI shows accurate state immediately AND the next
// scheduled chain isn't blocked by a stale in-progress sentinel.
/* Oct 2026 — Yahoo Finance health tile. Yahoo is blocked from the
   production egress IP (fees/MMF yields silently stopped for weeks before
   anyone noticed). One glance: live probe result, breaker state, last
   success, and what still leans on Yahoo. */
function YahooHealthTile() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/yahoo-health`, { withCredentials: true });
      setData(r.data);
    } catch (e) {
      setData({ probe: { ok: false, error: e?.response?.data?.detail || e.message } });
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);
  const ok = !!data?.probe?.ok;
  const guard = data?.guard || {};
  const dep = data?.dependents || {};
  const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : 'never (since boot)');
  return (
    <div
      className={`rounded-md border px-3 py-2 text-xs ${data == null ? 'border-border bg-muted/30' : ok
        ? 'border-emerald-300/70 dark:border-emerald-700/60 bg-emerald-50/70 dark:bg-emerald-950/25'
        : 'border-red-300/70 dark:border-red-700/60 bg-red-50/80 dark:bg-red-950/30'}`}
      data-testid="yahoo-health-tile"
    >
      <div className="flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 font-semibold">
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : ok
            ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
            : <XCircle className="w-3.5 h-3.5 text-red-600" />}
          <span data-testid="yahoo-health-status">
            Yahoo Finance from this server: {data == null ? 'checking…' : ok ? `REACHABLE · SPY $${data.probe.price} in ${data.probe.ms} ms` : `BLOCKED — ${data.probe?.error || 'no data'}`}
          </span>
        </div>
        <button type="button" onClick={load} className="underline underline-offset-2 text-muted-foreground hover:text-foreground" data-testid="yahoo-health-refresh">re-probe</button>
      </div>
      {data && (
        <div className="mt-1.5 grid grid-cols-1 sm:grid-cols-3 gap-x-4 gap-y-0.5 text-[11px] text-muted-foreground">
          <span data-testid="yahoo-health-guard">Breaker: {guard.open ? `OPEN (retries in ${Math.ceil((guard.reopens_in_sec || 0) / 60)} min)` : 'closed'} · {guard.consecutive_failures ?? 0} consecutive failures · opened {guard.opened_count ?? 0}×</span>
          <span data-testid="yahoo-health-last-success">Last success: {fmt(guard.last_success_at)}</span>
          <span data-testid="yahoo-health-last-failure">Last failure: {fmt(guard.last_failure_at)}</span>
          {dep.fees && (
            <span data-testid="yahoo-health-fees">Fees: {dep.fees.sec} from SEC · {dep.fees.yahoo_fallback} Yahoo fallback · {dep.fees.missing} ETFs missing</span>
          )}
          {dep.mmf && (
            <span data-testid="yahoo-health-mmf">MMF yields: {dep.mmf.sec_sourced}/{dep.mmf.funds} from SEC N-MFP · newest report {dep.mmf.newest_report || '—'}</span>
          )}
          {Array.isArray(dep.yahoo_only) && (
            <span className="sm:col-span-3" title={dep.yahoo_only.join(' · ')} data-testid="yahoo-health-dependents">
              Still Yahoo-only when blocked: {dep.yahoo_only.join(' · ')}
            </span>
          )}
        </div>
      )}
    </div>
  );
}


function ReapZombiesButton({ onAfterReap }) {
  const [busy, setBusy] = useState(false);
  const handleClick = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/admin/cron/reap-zombies`, {}, { withCredentials: true });
      const n = r?.data?.reaped || 0;
      if (n === 0) {
        toast.info('No zombies found', { description: 'All cron runs >1h old have a finish marker.' });
      } else {
        const names = (r?.data?.details || []).map((d) => d.label || d.job_key).join(', ');
        toast.success(`Reaped ${n} stuck run${n === 1 ? '' : 's'}`, {
          description: names || 'Marked as crashed_recovered.',
          duration: 5000,
        });
        onAfterReap?.();
      }
    } catch (e) {
      toast.error('Reap failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusy(false);
    }
  };
  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={busy}
      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
      title="Mark any 'running' cron-run row older than 1h as crashed_recovered. Use when a previous run died mid-execution (e.g. backend redeploy) and the UI shows it stuck on running."
      data-testid="reap-zombies-btn"
    >
      {busy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Skull className="w-3 h-3" />}
      Reap stuck runs
    </button>
  );
}


/**
 * WarrantsPipelinePanel — Round P140·3 (Feb 2026)
 *
 * Admin quick-check strip for the two Fri-morning warrant crons.
 * Ships next to ChainRunButtons + ReapZombiesButton so seeding
 * production after a fresh deploy is one click, not a curl command.
 *
 * Three buttons:
 *   1. "Seed 441 warrants"    → POST /admin/warrants/ingest-now
 *   2. "Watch 5 redemptions"  → POST /admin/warrants/watch-now?max_warrants=5
 *   3. "Show warrant stats"   → GET  /admin/warrants/stats (result rendered inline)
 */
function WarrantsPipelinePanel() {
  const [busyIngest, setBusyIngest] = useState(false);
  const [busyWatch, setBusyWatch] = useState(false);
  const [stats, setStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);

  const fetchStats = async () => {
    setLoadingStats(true);
    try {
      const r = await axios.get(`${API}/admin/warrants/stats`, { withCredentials: true });
      setStats(r.data);
    } catch (e) {
      toast.error('Warrant stats failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setLoadingStats(false);
    }
  };

  useEffect(() => {
    fetchStats();
  }, []);

  const runIngest = async () => {
    setBusyIngest(true);
    try {
      const { data } = await axios.post(`${API}/admin/warrants/ingest-now`, {}, { withCredentials: true });
      toast.success(`Ingested ${data.upserted} warrants`, {
        description: `Fetched ${data.fetched} · tagged ${data.stock_cache_tagged} stock_cache rows.`,
      });
      fetchStats();
    } catch (e) {
      toast.error('Warrant ingest failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusyIngest(false);
    }
  };

  const runWatch = async (max = 5) => {
    setBusyWatch(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/warrants/watch-now?max_warrants=${max}`,
        {},
        { withCredentials: true },
      );
      toast.success(`Watched ${data.checked} warrants`, {
        description: `${data.matched} matched · ${data.deadlines_stamped} deadline(s) stamped.`,
      });
      fetchStats();
    } catch (e) {
      toast.error('Warrant watcher failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setBusyWatch(false);
    }
  };

  return (
    <div
      className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 space-y-2"
      data-testid="admin-warrants-pipeline-panel"
    >
      <div className="flex items-center gap-2 text-xs font-bold">
        <FileText className="w-3.5 h-3.5 text-amber-600 dark:text-amber-400" />
        <span>Warrants Pipeline</span>
        <span className="text-[10px] font-normal text-muted-foreground">
          · Sat 08:00 ET chain: ingest → SPAC → watcher → expiry scraper
        </span>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-[11px]">
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Total</div>
            <div className="font-mono font-bold" data-testid="warrant-stats-total">{stats.total}</div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Redemption Announced</div>
            <div className="font-mono font-bold text-orange-700 dark:text-orange-400">
              {stats.by_status?.redemption_announced ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Redeemed</div>
            <div className="font-mono font-bold text-rose-700 dark:text-rose-400">
              {stats.by_status?.redeemed ?? 0}
            </div>
          </div>
          <div>
            <div className="text-[9px] uppercase tracking-wide text-muted-foreground">Missing CIK</div>
            <div className="font-mono font-bold text-muted-foreground">
              {stats.data_quality?.missing_cik ?? 0}
            </div>
          </div>
        </div>
      )}

      {stats?.last_synced_at && (
        <div className="text-[10px] text-muted-foreground">
          Last ingest: <span className="font-mono">{stats.last_synced_at.slice(0, 19).replace('T', ' ')}Z</span>
          {stats.last_watched_at && (
            <> · Last watch: <span className="font-mono">{stats.last_watched_at.slice(0, 19).replace('T', ' ')}Z</span></>
          )}
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={runIngest}
          disabled={busyIngest}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Fetch every active US warrant from Massive (type=WARRANT) and upsert into db.warrants + stock_cache. Use after a fresh deploy to seed production without waiting for Friday 04:00 UTC."
          data-testid="warrant-ingest-now-btn"
        >
          {busyIngest ? <Loader2 className="w-3 h-3 animate-spin" /> : <FileText className="w-3 h-3" />}
          Seed 441 warrants
        </button>
        <button
          type="button"
          onClick={() => runWatch(5)}
          disabled={busyWatch}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border border-orange-500/60 text-orange-700 dark:text-orange-400 hover:bg-orange-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Run the 8-K redemption watcher against the first 5 warrants. Smoke-test the Massive disclosures feed without waiting for Friday 06:00 UTC or running the full ~440-ticker sweep."
          data-testid="warrant-watch-5-btn"
        >
          {busyWatch ? <Loader2 className="w-3 h-3 animate-spin" /> : <Play className="w-3 h-3" />}
          Watch 5 redemptions
        </button>
        <button
          type="button"
          onClick={fetchStats}
          disabled={loadingStats}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border border-border/70 text-muted-foreground hover:bg-muted/40 disabled:opacity-50"
          title="Refresh the totals + last-run timestamps"
          data-testid="warrant-refresh-stats-btn"
        >
          {loadingStats ? <Loader2 className="w-3 h-3 animate-spin" /> : <Clock className="w-3 h-3" />}
          Refresh stats
        </button>
      </div>

      {Array.isArray(stats?.fresh_events) && stats.fresh_events.length > 0 && (
        <div className="mt-2 border-t border-border/40 pt-2">
          <div className="text-[10px] font-bold uppercase tracking-wide text-muted-foreground mb-1">
            Recent Redemption Events
          </div>
          <ul className="space-y-0.5 text-[11px] font-mono">
            {stats.fresh_events.slice(0, 5).map((ev) => (
              <li key={ev.ticker} className="flex items-center gap-2">
                <span className="font-bold text-foreground">{ev.ticker}</span>
                <span className="text-muted-foreground">{ev.status}</span>
                {ev.redemption_deadline && <span className="text-orange-700 dark:text-orange-400">→ {ev.redemption_deadline}</span>}
                {ev.redemption_price != null && <span className="text-rose-700 dark:text-rose-400">@ ${ev.redemption_price.toFixed(2)}</span>}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}


export default function AdminCronHistoryDropdown() {
  const [open, setOpen] = useState(false);
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/admin/cron-history`, { withCredentials: true });
      setRuns(Array.isArray(r.data?.runs) ? r.data.runs : []);
    } catch {
      setRuns([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchHistory();
    const id = setInterval(fetchHistory, 60_000);
    return () => clearInterval(id);
  }, [open]);

  // Banner alert: any latest run NOT in {"ok", "running"} → amber.
  const failedCount = useMemo(
    () => runs.filter(r => r.latest && !['ok', 'running'].includes(r.latest.result)).length,
    [runs],
  );

  // Round P108ao — group "currently running" jobs by their chain so
  // the chain-fire buttons can render as disabled when the target
  // chain already has a live job. Chain string is carried on each
  // row from the backend catalog (daily/weekly/monthly).
  const runningByChain = useMemo(() => {
    const map = {};
    for (const r of runs) {
      const latest = r?.latest;
      if (latest && latest.result === 'running' && !latest.finished_at) {
        const c = (r.chain || '').toLowerCase();
        if (c) map[c] = true;
      }
    }
    return map;
  }, [runs]);

  return (
    <div className="rounded-lg border border-border bg-card" data-testid="admin-cron-history">
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 hover:bg-muted/30 transition-colors"
        data-testid="admin-cron-history-toggle"
      >
        <span className="inline-flex items-center gap-2 text-sm font-bold">
          <Calendar className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          Cron Job History
          <span className="text-[10px] font-normal text-muted-foreground">
            · daily 2am Mon–Fri · weekly Sun 3am · warrants Sat 8am
          </span>
          {failedCount > 0 && (
            <span className="ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold bg-amber-100 dark:bg-amber-950/40 text-amber-700 dark:text-amber-300 border border-amber-300/60 dark:border-amber-800/60">
              ⚠ {failedCount} not OK
            </span>
          )}
        </span>
        {open ? <ChevronUp className="w-4 h-4 text-muted-foreground" /> : <ChevronDown className="w-4 h-4 text-muted-foreground" />}
      </button>

      {open && (
        <div className="border-t border-border px-3 py-3 space-y-2">
          {/* On-demand chain runners — Round 92o++++++++N+++++++++++.
              Replaces the deprecated per-warmer "Fire" buttons. Useful
              when a code change requires re-bucketing data the natural
              cron won't touch for days, or when retrying a stalled
              weekly chain without waiting until Sunday. */}
          <YahooHealthTile />
          <ChainRunButtons onAfterRun={fetchHistory} runningByChain={runningByChain} />
          {/* Round 94k — Manual zombie reaper. Marks any "running" row
              that has no finished_at and started >1h ago as
              `crashed_recovered`, unblocking the next scheduled chain
              run when a previous one died mid-execution (e.g. backend
              redeploy). */}
          <ReapZombiesButton onAfterReap={fetchHistory} />
          {/* Round P140·3 (Feb 2026) — Warrants pipeline quick-check.
              Seeds production in one click after a fresh deploy and
              lets an admin smoke-test the 8-K watcher against 5
              warrants without waiting for Friday 06:00 UTC. */}
          <WarrantsPipelinePanel />
          {loading && runs.length === 0 ? (
            <p className="text-xs text-muted-foreground">Loading history…</p>
          ) : runs.length === 0 ? (
            <p className="text-xs text-muted-foreground italic">
              No cron runs recorded yet. First daily run fires next weekday at 2 AM ET.
            </p>
          ) : (
            runs.map((row) => {
              const latest = row.latest || {};
              const resultKey = latest.result || (row.never_run ? 'pending' : 'error');
              const style = RESULT_STYLES[resultKey] || RESULT_STYLES.error;
              const Icon = style.icon;
              return (
                <div
                  key={row.job_key}
                  className="rounded-md border bg-background/50 p-2.5"
                  data-testid={`cron-row-${row.job_key}`}
                >
                  <div className="flex items-center justify-between gap-3 mb-1.5">
                    <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                      <Icon className={`w-4 h-4 flex-shrink-0 ${style.color}`} />
                      <span className="text-sm font-bold truncate">{row.label}</span>
                      <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-muted text-muted-foreground">
                        {row.chain || '?'}
                      </span>
                      {row.never_run && (
                        <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide bg-zinc-200/60 dark:bg-zinc-800/60 text-zinc-600 dark:text-zinc-400 border border-zinc-300/60 dark:border-zinc-700/60">
                          never run
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <MiniSparkline runs={row.recent_7} />
                      <SingleJobRunButton
                        jobKey={row.job_key}
                        label={row.label}
                        onAfterRun={fetchHistory}
                        isRunning={latest.result === 'running' && !latest.finished_at}
                      />
                    </div>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-1 text-[11px]">
                    <div>
                      <span className="text-muted-foreground">Started: </span>
                      <span className="font-mono">{fmtClock(latest.started_at)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Finished: </span>
                      <span className="font-mono">{fmtClock(latest.finished_at)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Duration: </span>
                      <span className="font-mono">{fmtDuration(latest.elapsed_s)}</span>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Processed: </span>
                      <span className="font-mono">
                        {latest.processed != null && latest.total
                          ? `${latest.processed}/${latest.total}`
                          : latest.processed || '—'}
                      </span>
                    </div>
                  </div>
                  {row.coverage && (
                    <div className="mt-1.5" data-testid="cron-governance-coverage">
                      <div className="flex items-center justify-between text-[10px]">
                        <span className="font-mono text-emerald-700 dark:text-emerald-300">
                          fetched {row.coverage.fetched.toLocaleString()} of {row.coverage.total.toLocaleString()}
                          {row.coverage.failed ? ` · ${row.coverage.failed} failed` : ''}
                          {row.coverage.remaining > 0
                            ? ` · ${row.coverage.nights_left != null ? `~${row.coverage.nights_left} night${row.coverage.nights_left === 1 ? '' : 's'} left` : `${row.coverage.remaining.toLocaleString()} to go`}`
                            : ' · caught up'}
                        </span>
                        <span className="font-mono text-muted-foreground">{row.coverage.pct}%</span>
                      </div>
                      <div className="mt-0.5 h-1.5 w-full rounded bg-muted/60 overflow-hidden">
                        <div className="h-full bg-emerald-500 transition-[width]" style={{ width: `${Math.min(100, row.coverage.pct)}%` }} />
                      </div>
                      <div className="text-[9px] text-muted-foreground mt-0.5">40 min per night · resumes where it stopped · a ticker stays fresh {row.coverage.fresh_days} days{row.coverage.per_night ? ` · ~${row.coverage.per_night}/night at last pace` : ''}</div>
                    </div>
                  )}
                  {latest.error && (
                    <p data-testid={`cron-run-error-${latest.job_key}`} className="mt-1 text-[10px] text-rose-600 dark:text-rose-400 font-mono break-words" title={latest.error}>
                      {latest.error}
                    </p>
                  )}
                  {latest.attempt > 1 && (
                    <p className="mt-1 text-[10px] text-amber-600 dark:text-amber-400">
                      Retried — final attempt #{latest.attempt}
                    </p>
                  )}
                </div>
              );
            })
          )}
          <p className="text-[10px] text-muted-foreground italic pt-1">
            Auto-refreshes every 60s while open.
          </p>
          <OtherScheduledJobs open={open} />
        </div>
      )}
    </div>
  );
}

/**
 * OtherScheduledJobs — Round 102n (Feb 2026) · P16 upgrade (Feb 2026).
 *
 * Every APScheduler job that isn't already wrapped by the daily
 * or weekly chain orchestrator. Lets an admin manually fire
 * independent warmers (Massive overlay, Squeeze Radar, Form 3,
 * IPO, filing/splits alerts, ratios refresh, quarterly financials,
 * yield/health caches, share-snapshot rebuild, etc.) that would
 * otherwise only fire on their natural APScheduler triggers.
 *
 * P16 — Each row now shows the same rich Start / End / Duration /
 * Complete-or-Failed line as the top-section chain jobs. Status is
 * sourced from `admin_jobs` keyed by the APScheduler `id`. Jobs that
 * have never fired since deploy render dashes with a neutral pill.
 */
function OtherScheduledJobs({ open }) {
  const [jobs, setJobs] = useState([]);
  const [busy, setBusy] = useState(null); // job_id currently firing
  const [confirming, setConfirming] = useState(null);
  // Round P135 (Feb 2026) — Reap-stuck state.
  // Round P135b (Feb 2026) — Aligned with the top-section
  // `ReapZombiesButton` pattern: single-click, toast-based feedback,
  // Skull + Loader2 icons, amber styling. No two-step confirm — the
  // top reaper doesn't need one and this one shouldn't either.
  const [reapBusy, setReapBusy] = useState(false);

  const fetchJobs = () => {
    return axios.get(`${API}/admin/scheduler/jobs`, { withCredentials: true })
      .then((r) => setJobs(Array.isArray(r.data?.jobs) ? r.data.jobs : []))
      .catch(() => setJobs([]));
  };

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    fetchJobs();
    // P16 — poll every 60s while the dropdown is open so a manually
    // fired job's Started/Finished timestamps refresh live without
    // requiring the admin to close & re-open the panel.
    // Aug 2026 — When any job is `in_progress`, tighten the poll to
    // 5 s so the progress chip visibly advances instead of stepping
    // once a minute. Falls back to 60 s once every job is idle again.
    let intervalMs = 60_000;
    let id = setInterval(() => { if (!cancelled) fetchJobs(); }, intervalMs);
    const adaptInterval = () => {
      const busy = jobs.some((j) => j.in_progress);
      const want = busy ? 5_000 : 60_000;
      if (want !== intervalMs) {
        clearInterval(id);
        intervalMs = want;
        id = setInterval(() => { if (!cancelled) fetchJobs(); }, intervalMs);
      }
    };
    const idAdapt = setInterval(adaptInterval, 2_000);
    return () => { cancelled = true; clearInterval(id); clearInterval(idAdapt); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Aug 2026 — Whitelist of jobs that should block the button until
  // the job finishes. All are known to complete in well under 60 s
  // and produce user-visible DB changes the admin needs to see on
  // next reload (e.g. `nightly_perf_refresh` rewrites every row's
  // perf_1m/6m/1y values). Long jobs stay fire-and-forget.
  const AWAIT_ON_MANUAL_RUN = new Set(['nightly_perf_refresh']);

  const fireJob = async (jobId) => {
    if (confirming !== jobId) { setConfirming(jobId); return; }
    setBusy(jobId);
    setConfirming(null);
    const shouldAwait = AWAIT_ON_MANUAL_RUN.has(jobId);
    try {
      const url = `${API}/admin/scheduler/run/${jobId}${shouldAwait ? '?await_completion=1' : ''}`;
      // Fast jobs get a generous timeout so the axios call itself
      // doesn't kill the blocking request mid-flight.
      const res = await axios.post(url, {}, {
        withCredentials: true,
        timeout: shouldAwait ? 70_000 : 15_000,
      });
      if (shouldAwait && res?.data?.completed) {
        toast.success(`${jobId} finished — reload the list to see fresh values.`, {
          duration: 6000,
        });
        // Notify any parent list-owning components that DB rows may
        // have changed so they can refetch without a full page reload.
        window.dispatchEvent(new CustomEvent('stock-cache-refreshed', {
          detail: { job: jobId, at: new Date().toISOString() },
        }));
      }
      // Refresh a few seconds later so the "Started" timestamp
      // shows up while the job is still in-flight.
      setTimeout(() => { fetchJobs(); }, 3_500);
    } catch (err) {
      console.error(`Fire scheduler job failed for ${jobId}:`, err);
    } finally {
      setTimeout(() => setBusy(null), 4000);
    }
  };

  const reapStuck = async () => {
    setReapBusy(true);
    try {
      const r = await axios.post(`${API}/admin/scheduler/reap-stuck`, {}, { withCredentials: true });
      const n = r?.data?.reaped || 0;
      if (n === 0) {
        toast.info('No stuck jobs found', { description: 'All jobs are either running fresh or already complete.' });
      } else {
        const keys = (r?.data?.reaped_keys || []).join(', ');
        toast.success(`Reaped ${n} stuck job${n === 1 ? '' : 's'}`, {
          description: keys || 'Cleared in_progress flags older than 30 min.',
          duration: 5000,
        });
        fetchJobs();
      }
    } catch (e) {
      toast.error('Reap failed', { description: e?.response?.data?.detail || e?.message });
    } finally {
      setReapBusy(false);
    }
  };

  if (jobs.length === 0) return null;

  // P16 — pill style: green Complete / red Failed / sky Running /
  // zinc "Never run". Matches the top-section RESULT_STYLES palette.
  const pillFor = (j) => {
    if (j.in_progress) {
      return { label: 'Running', cls: 'bg-sky-500/15 text-sky-700 dark:text-sky-300 border-sky-500/40' };
    }
    if (j.last_status === 'complete') {
      return { label: 'Complete', cls: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300 border-emerald-500/40' };
    }
    if (j.last_status === 'failed') {
      return { label: 'Failed', cls: 'bg-rose-500/15 text-rose-700 dark:text-rose-300 border-rose-500/40' };
    }
    if (j.last_status === 'aborted') {
      return { label: 'Aborted · prior data kept', cls: 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40' };
    }
    return { label: 'Never run', cls: 'bg-zinc-200/60 dark:bg-zinc-800/60 text-zinc-600 dark:text-zinc-400 border-zinc-300/60 dark:border-zinc-700/60' };
  };

  return (
    <div className="mt-3 pt-3 border-t border-border/60" data-testid="other-scheduled-jobs">
      <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
        <h4 className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
          Other Scheduled Jobs ({jobs.length})
          <span className="ml-2 text-[10px] font-normal normal-case text-muted-foreground/70">
            — independent APScheduler triggers not in the daily/weekly chain
          </span>
        </h4>
        {/* Round P135b (Feb 2026) — Reap button. Same styling and
            flow as the top-section `ReapZombiesButton` per user
            preference (single-click, toast feedback, amber Skull). */}
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); reapStuck(); }}
          disabled={reapBusy}
          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[11px] font-bold border border-amber-500/60 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10 disabled:opacity-50 disabled:cursor-not-allowed"
          title="Clear in_progress flags on admin_jobs rows stuck > 30 min (backend restart / OOM recovery)"
          data-testid="other-scheduler-reap-stuck-btn"
        >
          {reapBusy ? <Loader2 className="w-3 h-3 animate-spin" /> : <Skull className="w-3 h-3" />}
          Reap stuck jobs
        </button>
      </div>
      <div className="space-y-1.5">
        {jobs.map((j) => {
          const isBusy = busy === j.id;
          const isConfirm = confirming === j.id;
          const pill = pillFor(j);
          return (
            <div
              key={j.id}
              className="rounded-md border bg-background/50 px-2.5 py-2"
              data-testid={`scheduler-job-${j.id}`}
            >
              <div className="flex items-center justify-between gap-3 mb-1">
                <div className="flex items-center gap-2 min-w-0 flex-wrap">
                  <span className="font-mono text-[11px] font-bold truncate">{j.name}</span>
                  <span
                    className={`px-1.5 py-0.5 rounded text-[9px] font-bold uppercase tracking-wide border whitespace-nowrap ${pill.cls}`}
                    data-testid={`scheduler-job-pill-${j.id}`}
                  >
                    {pill.label}
                  </span>
                </div>
                <button
                  type="button"
                  onClick={(e) => { e.stopPropagation(); fireJob(j.id); }}
                  disabled={isBusy || j.in_progress}
                  className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-bold border transition-colors whitespace-nowrap ${
                    isBusy ? 'bg-sky-500/20 text-sky-700 border-sky-400'
                    : isConfirm ? 'bg-rose-600 text-white border-rose-700 hover:bg-rose-700'
                    : 'bg-background border-border hover:border-violet-400 hover:text-violet-600 dark:hover:text-violet-300'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                  data-testid={`scheduler-job-run-${j.id}`}
                  title={j.in_progress ? 'Job is running — button re-enables when it finishes.' : ''}
                >
                  {j.in_progress ? 'Running…' : isBusy ? 'Starting…' : isConfirm ? 'Confirm' : '▶ Run'}
                </button>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-3 gap-y-0.5 text-[10.5px]">
                <div>
                  <span className="text-muted-foreground">Next: </span>
                  <span className="font-mono">{j.next_run ? new Date(j.next_run).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Started: </span>
                  <span className="font-mono">{fmtClock(j.last_started_at)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Ended: </span>
                  <span className="font-mono">{fmtClock(j.last_completed_at)}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Duration: </span>
                  <span className="font-mono">{fmtDuration(j.last_elapsed_seconds)}</span>
                </div>
              </div>
              {j.last_error && (
                <p
                  className="mt-1 text-[10px] text-rose-600 dark:text-rose-400 font-mono truncate"
                  title={j.last_error}
                  data-testid={`scheduler-job-error-${j.id}`}
                >
                  {j.last_error}
                </p>
              )}
              {j.last_result && !j.in_progress && (
                <p
                  className={`mt-1 text-[10px] font-mono break-words ${j.last_status === 'aborted' ? 'text-amber-600 dark:text-amber-400' : 'text-muted-foreground'}`}
                  data-testid={`scheduler-job-result-${j.id}`}
                >
                  Last run: {j.last_result}
                </p>
              )}
              {j.in_progress && j.progress && j.progress.total > 0 && (
                <div className="mt-1.5" data-testid={`scheduler-job-progress-${j.id}`}>
                  <div className="flex items-center justify-between text-[10px] font-mono mb-0.5">
                    <span className="text-violet-600 dark:text-violet-400">
                      {j.progress.done.toLocaleString()}
                      {' / '}
                      {j.progress.total.toLocaleString()}
                      {j.progress.pct != null && (
                        <span className="text-muted-foreground"> · {j.progress.pct}%</span>
                      )}
                    </span>
                    {j.progress.phase && (
                      <span className="text-muted-foreground truncate ml-2">{j.progress.phase}</span>
                    )}
                  </div>
                  <div className="w-full h-1 bg-muted/40 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-violet-500 dark:bg-violet-400 transition-all"
                      style={{ width: `${Math.min(100, Math.max(0, j.progress.pct || 0))}%` }}
                    />
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="text-[10px] text-muted-foreground italic pt-2">
        Auto-refreshes every 60s while open. Manually-fired jobs log Start / End / Duration to <span className="font-mono">admin_jobs</span>.
      </p>
    </div>
  );
}
